import * as bootstrap from 'bootstrap';
import TomSelect from 'tom-select';
import flatpickr from 'flatpickr';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import newPassword from './helpers/new-password.js';
import { registerGlobalTomSelectHelpers } from "./helpers/plugins/tom-select.js";

registerGlobalTomSelectHelpers();
window.flatpickr = flatpickr;
window.Dutch = Dutch;

class Portaal
{
    constructor()
    {
        this.init();
        this.bindLivewireHooks();
    }

    init(container = document)
    {
        this.initDVSSelector(container);
        this.initProgressBars(container);
        this.initFileUploads(container);
        this.initExpandableRemarks(container);
        this.initTooltip(container);
        this.initTomSelect(container);
        this.initDatePicker(container);
        this.initPasswordToggle(container);
        this.initContactFormValidation(container);
        newPassword(container);
    }

    initDVSSelector(container = document)
    {
        const dvsSelector = container.id === 'dvs-selector'
            ? container
            : container.querySelector('#dvs-selector');

        if (!dvsSelector)
        {
            return;
        }

        dvsSelector.querySelectorAll('.select-dvs').forEach((selectDvs) => {
            if (selectDvs.dataset.portaalDvsBound === '1')
            {
                return;
            }

            selectDvs.dataset.portaalDvsBound = '1';

            selectDvs.addEventListener('click', () => {
                this.submitDVSSelection(selectDvs);
            });

            selectDvs.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter' && event.key !== ' ')
                {
                    return;
                }

                event.preventDefault();
                this.submitDVSSelection(selectDvs);
            });
        });
    }

    submitDVSSelection(selectDvs)
    {
        const form = selectDvs.querySelector('.form-select-dvs');

        if (!form)
        {
            console.error("[Portaal] No '.form-select-dvs' element found within selected tile.");
            return;
        }

        form.submit();
    }

    initProgressBars(container = document)
    {
        const progressBars = container.querySelectorAll('.progress-bar');

        if (progressBars.length === 0)
        {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting)
                {
                    return;
                }

                const bar = entry.target;
                const text = bar.querySelector('.text');
                const target = parseInt(bar.dataset.target || '0', 10);

                bar.style.transition = 'width 1s ease';
                bar.style.width = `${target}%`;
                bar.setAttribute('aria-valuenow', target.toString());

                if (text)
                {
                    text.innerText = `${target}%`;
                }

                observer.unobserve(bar);
            });
        }, { threshold: 0.5 });

        progressBars.forEach((bar) => {
            observer.observe(bar);
        });
    }

    initFileUploads(container = document)
    {
        const fileInputs = container.querySelectorAll('input[type="file"]');

        fileInputs.forEach((input) => {
            if (input.dataset.portaalUploadBound === '1')
            {
                return;
            }

            input.dataset.portaalUploadBound = '1';

            input.addEventListener('change', (event) => {
                const target = event.target;
                const wrapper = target.closest('.input-wrapper');

                if (!wrapper)
                {
                    return;
                }

                const formUploadText = wrapper.querySelector('.form-upload__text');

                if (!formUploadText)
                {
                    return;
                }

                if (!target.files || target.files.length === 0)
                {
                    formUploadText.innerText = '';
                    return;
                }

                formUploadText.innerText = target.files[0].name;
            });
        });
    }

    /**
     * Initialize remark content that expands when it exceeds the configured rendered line count.
     */
    initExpandableRemarks(container = document) {
        // Livewire may pass either the complete document or a newly morphed element.
        const expandableRemarks = container instanceof Element && container.matches('[data-expandable-remark]')
            ? [container]
            : container.querySelectorAll('[data-expandable-remark]');

        expandableRemarks.forEach((remark) => {
            // Prevent duplicate event listeners when Livewire initializes the same element again.
            if (remark.dataset.portaalExpandableRemarkBound === '1') {
                return;
            }

            const content = remark.querySelector('[data-remark-content]');
            const toggle = remark.querySelector('[data-remark-toggle]');

            if (!content || !toggle) {
                return;
            }

            remark.dataset.portaalExpandableRemarkBound = '1';
            remark.classList.add('portal-remark--collapsible');

            const collapsedLines = parseInt(remark.dataset.collapsedLines || '10', 10);
            let expanded = false;

            // Keep the visual state, accessible state, and button label synchronized.
            const setExpanded = (shouldExpand) => {
                expanded = shouldExpand;
                remark.classList.toggle('portal-remark--expanded', expanded);
                toggle.textContent = expanded ? 'Minder tonen' : 'Meer tonen';
                toggle.setAttribute('aria-expanded', expanded.toString());
            };

            const measure = () => {
                if (content.clientWidth === 0) {
                    return;
                }

                const lineHeight = parseFloat(window.getComputedStyle(content).lineHeight);

                if (!Number.isFinite(lineHeight)) {
                    return;
                }

                const collapsedHeight = lineHeight * collapsedLines;
                // scrollHeight contains the full text height, even while max-height clips it.
                const expandedHeight = content.scrollHeight;
                const overflows = expandedHeight > collapsedHeight + 1;

                // CSS custom properties allow both directions to animate between exact heights.
                content.style.setProperty('--portal-remark-collapsed-height', `${collapsedHeight}px`);
                content.style.setProperty('--portal-remark-expanded-height', `${expandedHeight}px`);
                toggle.hidden = !overflows;

                if (!overflows) {
                    setExpanded(false);
                }
            };

            toggle.addEventListener('click', () => {
                setExpanded(!expanded);
            });

            const collapse = remark.closest('.accordion-collapse');

            // A closed accordion has no measurable width, so measure once Bootstrap has opened it.
            if (collapse) {
                collapse.addEventListener('shown.bs.collapse', measure);
            }

            // Recalculate wrapped line count when the content width changes responsively.
            if (typeof ResizeObserver === 'function') {
                const resizeObserver = new ResizeObserver(measure);
                resizeObserver.observe(content);
                remark.portaalRemarkResizeObserver = resizeObserver;
            }

            // Also measure immediately when the accordion was already open during initialization.
            if (collapse?.classList.contains('show')) {
                requestAnimationFrame(measure);
            }
        });
    }

    initTooltip(container = document)
    {
        container.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
            const instance = bootstrap.Tooltip.getInstance(element);

            if (!instance)
            {
                new bootstrap.Tooltip(element, {
                    trigger: 'hover',
                });
            }
        });
    }

    initTomSelect(container = document)
    {
        // Create a new instance for every dropdown with the multiple option
        container.querySelectorAll('select.multi-select').forEach(element => {
            new TomSelect(element, {
                closeAfterSelect: false,
                hideSelected: true,
                maxOptions: null,
                plugins: [
                    'remove_button',
                    'clear_button'
                ],
            });
        });

        // Create a new instance for every default dropdown
        container.querySelectorAll('select:not(.multi-select):not(.livewire-select):not([data-native-select]):not(.flatpickr-month select):not(.flatpickr-year select)').forEach(element => {
            new TomSelect(element, {
                maxOptions: null,
                plugins: [
                    'clear_button'
                ],
            });
        });
    }

    initDatePicker(container = document)
    {
        container.querySelectorAll('.datepicker').forEach((input) => {
            if (input._flatpickr)
            {
                return;
            }

            let defaultDate = null;

            if (input.value.length > 0)
            {
                const dateParts = input.value.split('-');

                if (dateParts.length === 3)
                {
                    defaultDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                }
            }

            flatpickr(input, {
                defaultDate,
                dateFormat: 'd-m-Y',
                locale: Dutch,
            });

            if (input.dataset.portaalDateKeypressBound !== '1')
            {
                input.dataset.portaalDateKeypressBound = '1';
                input.addEventListener('keypress', (event) => {
                    event.preventDefault();
                });
            }
        });
    }

    initPasswordToggle(container = document)
    {
        container.querySelectorAll('.toggle-password').forEach((toggle) => {
            if (toggle.dataset.portaalPasswordToggleBound === '1')
            {
                return;
            }

            toggle.dataset.portaalPasswordToggleBound = '1';

            toggle.addEventListener('click', () => {
                const inputPassword = toggle.closest('.input-wrapper')?.querySelector('input');

                if (!inputPassword)
                {
                    return;
                }

                inputPassword.type = inputPassword.type === 'text' ? 'password' : 'text';

                const icon = toggle.querySelector('i');

                if (!icon)
                {
                    return;
                }

                icon.classList.toggle('fa-eye');
                icon.classList.toggle('fa-eye-slash');
            });
        });
    }

    initContactFormValidation(container = document)
    {
        const form = container.id === 'support-form'
            ? container
            : container.querySelector('#support-form');

        if (!form)
        {
            return;
        }

        const submitButton = form.querySelector('button[type="submit"]');

        if (!submitButton)
        {
            return;
        }

        const requiredFields = form.querySelectorAll('input[name="name"], input[name="email"], textarea[name="message"]');

        const checkFields = () => {
            let allFilled = true;

            requiredFields.forEach((field) => {
                if (!field.value.trim())
                {
                    allFilled = false;
                }
            });

            submitButton.disabled = !allFilled;
        };

        checkFields();

        requiredFields.forEach((field) => {
            if (field.dataset.portaalContactValidationBound === '1')
            {
                return;
            }

            field.dataset.portaalContactValidationBound = '1';
            field.addEventListener('input', checkFields);
        });
    }

    bindLivewireHooks()
    {
        document.addEventListener('livewire:init', () => {
            if (!window.Livewire || typeof window.Livewire.hook !== 'function')
            {
                return;
            }

            window.Livewire.hook('morphed', ({ el }) => {
                this.init(el);
            });
        });

        if (window.Livewire && typeof window.Livewire.on === 'function')
        {
            window.Livewire.on('validation-failed', () => {
                this.scrollToFirstError();
            });
        }
    }

    scrollToFirstError()
    {
        requestAnimationFrame(() => {
            const firstError = document.querySelector('.form-error');

            if (!firstError)
            {
                return;
            }

            const formGroup = firstError.closest('.form-group');
            const scrollTarget = formGroup || firstError;

            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });

            const focusTarget = scrollTarget.querySelector(
                '.ts-control input, input:not([type="hidden"]):not([type="file"]), textarea, select'
            );

            if (focusTarget)
            {
                setTimeout(() => focusTarget.focus(), 300);
            }
        });
    }
}

window.addEventListener('load', () => {
    window.portaal = new Portaal();
});
